import {
  CheckCircle2,
  ExternalLink,
  Facebook,
  Inbox,
  Instagram,
  Loader2,
  MessageCircle,
  Moon,
  Pencil,
} from "lucide-react";
import {
  useCanAccess,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
} from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useAgents } from "../useAgents";
import {
  nextCheckAt,
  socialStatus,
  timeAgo,
  type SocialAlert,
  type SocialChannel,
  type SocialChannelKey,
  type SocialCheck,
  type SocialSettings,
  type SocialStatus,
} from "./socialInbox";

/** TikTok is not in the icon set: a simple note glyph in its place. */
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M16.6 3c.4 2.3 1.9 3.9 4.1 4.2v3.2c-1.5 0-2.9-.4-4.1-1.2v6.1c0 3.4-2.7 5.9-5.9 5.9S4.8 18.7 4.8 15.3s2.7-5.9 5.9-5.9c.3 0 .6 0 .9.1v3.3c-.3-.1-.6-.2-.9-.2-1.5 0-2.7 1.2-2.7 2.7s1.2 2.7 2.7 2.7 2.7-1.2 2.7-2.7V3h3.2z" />
  </svg>
);

const ICON: Record<
  SocialChannelKey,
  React.ComponentType<{ className?: string }>
> = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTokIcon,
};

const STATUS_TEXT: Record<SocialStatus, string> = {
  inactive: "Aún sin cuenta",
  alerts: "Comentarios sin responder",
  due: "Toca revisar la bandeja",
  ok: "Al día",
  closed: "Fuera de horario",
};

const hhmm = (date: Date) =>
  date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

const ChannelCard = ({
  channel,
  status,
  lastCheck,
  alerts,
  next,
  isAdmin,
  onChecked,
  onChannelChanged,
}: {
  channel: SocialChannel;
  status: SocialStatus;
  lastCheck?: SocialCheck;
  alerts: SocialAlert[];
  next: Date | null;
  isAdmin: boolean;
  onChecked: () => void;
  onChannelChanged: () => void;
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { nameOf } = useAgents();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [link, setLink] = useState(channel.inbox_url ?? "");
  const Icon = ICON[channel.key];
  const urgent = status === "due" || status === "alerts";

  const markChecked = async () => {
    setBusy(true);
    try {
      const { data, error } = await getSupabaseClient().rpc(
        "gula_social_mark_checked",
        { p_channel: channel.key },
      );
      if (error) throw error;
      notify(
        data
          ? `${channel.label}: ${data} comentario${data === 1 ? "" : "s"} respondido${data === 1 ? "" : "s"}`
          : `${channel.label}: bandejas al día`,
        { type: "success" },
      );
      onChecked();
    } catch (error) {
      console.error(error);
      notify("No se ha podido marcar", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const saveChannel = async (patch: Partial<SocialChannel>) => {
    try {
      await dataProvider.update("gula_social_channels", {
        id: channel.key,
        data: patch,
        previousData: channel,
      });
      setEditing(false);
      onChannelChanged();
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar", { type: "error" });
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-44 flex-col gap-3 rounded-3xl p-5 transition-colors",
        urgent
          ? "bg-lime-200 text-neutral-900"
          : status === "inactive"
            ? "border border-dashed text-muted-foreground"
            : "border bg-muted/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-2 text-lg font-semibold">
          <Icon className="h-5 w-5" />
          {channel.label}
        </span>
        {status === "alerts" ? (
          <span className="rounded-full bg-neutral-900 px-2.5 py-0.5 text-xs font-bold text-lime-300">
            {alerts.length} nuevo{alerts.length === 1 ? "" : "s"}
          </span>
        ) : status === "ok" ? (
          <CheckCircle2 className="h-5 w-5 text-lime-600 dark:text-lime-400" />
        ) : status === "closed" ? (
          <Moon className="h-4 w-4 text-muted-foreground" />
        ) : null}
      </div>

      <div className="flex flex-col gap-0.5">
        <span
          className={cn(
            "text-sm font-medium",
            !urgent && "text-foreground",
            status === "inactive" && "text-muted-foreground",
          )}
        >
          {STATUS_TEXT[status]}
        </span>
        <span
          className={cn(
            "text-xs",
            urgent ? "text-neutral-700" : "text-muted-foreground",
          )}
        >
          {status === "inactive"
            ? "Cuando abráis la cuenta, actívala aquí."
            : lastCheck
              ? `Revisado por ${nameOf(lastCheck.sales_id).split(" ")[0] || "alguien"} ${timeAgo(lastCheck.created_at)}${
                  status === "ok" && next ? ` · próxima ${hhmm(next)}` : ""
                }`
              : "Hoy nadie la ha revisado"}
        </span>
      </div>

      {alerts.length ? (
        <ul className="flex flex-col gap-1">
          {alerts.slice(0, 3).map((alert) => (
            <li key={alert.id}>
              <a
                href={alert.url ?? channel.inbox_url ?? "#"}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-1.5 rounded-xl bg-white/60 px-2 py-1.5 text-xs hover:bg-white"
              >
                <MessageCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="line-clamp-2">
                  {alert.author ? <strong>{alert.author}: </strong> : null}
                  {alert.body ?? "Nuevo comentario"}
                  <span className="text-neutral-500">
                    {" "}
                    · {timeAgo(alert.created_at)}
                  </span>
                </span>
              </a>
            </li>
          ))}
          {alerts.length > 3 ? (
            <li className="px-2 text-xs text-neutral-600">
              y {alerts.length - 3} más
            </li>
          ) : null}
        </ul>
      ) : null}

      {editing ? (
        <div className="flex flex-col gap-2">
          <Input
            value={link}
            onChange={(event) => setLink(event.target.value)}
            placeholder="Enlace de la bandeja (https://…)"
            className="h-8 bg-background text-xs"
          />
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              className="h-7"
              onClick={() => saveChannel({ inbox_url: link.trim() || null })}
            >
              Guardar
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => saveChannel({ active: !channel.active })}
            >
              {channel.active ? "Desactivar" : "Activar"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => setEditing(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-auto flex flex-wrap items-center gap-2">
          {status !== "inactive" ? (
            <>
              <Button
                size="sm"
                className={cn(
                  "rounded-full",
                  urgent
                    ? "bg-neutral-900 text-white hover:bg-neutral-800"
                    : "",
                )}
                variant={urgent ? "default" : "outline"}
                disabled={busy}
                onClick={markChecked}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Respondidas
              </Button>
              {channel.inbox_url ? (
                <Button
                  asChild
                  size="sm"
                  variant="ghost"
                  className={cn(
                    "rounded-full",
                    urgent && "hover:bg-white/60 hover:text-neutral-900",
                  )}
                >
                  <a href={channel.inbox_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    Abrir bandeja
                  </a>
                </Button>
              ) : null}
            </>
          ) : null}
          {isAdmin ? (
            <button
              type="button"
              onClick={() => {
                setLink(channel.inbox_url ?? "");
                setEditing(true);
              }}
              className={cn(
                "ml-auto rounded-full p-1.5",
                urgent
                  ? "text-neutral-700 hover:bg-white/60"
                  : "text-muted-foreground hover:bg-muted",
              )}
              title="Cambiar enlace o activar"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

/**
 * "Bandejas de redes" in Mi jornada: one square per network for the people
 * who answer comments and messages (Joaquín and Mica by default).
 */
export const SocialInboxCards = () => {
  const { identity } = useGetIdentity();
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "money",
    action: "list",
  });
  const { data: settings } = useGetOne<SocialSettings>("gula_social_settings", {
    id: 1,
  });
  const isOwner =
    !!identity &&
    !!settings?.owner_ids?.some((id) => String(id) === String(identity.id));

  const { data: channels = [], refetch: refetchChannels } =
    useGetList<SocialChannel>(
      "gula_social_channels",
      {
        pagination: { page: 1, perPage: 10 },
        sort: { field: "sort", order: "ASC" },
      },
      { enabled: isOwner },
    );
  const { data: checks = [], refetch: refetchChecks } = useGetList<SocialCheck>(
    "gula_social_checks",
    {
      pagination: { page: 1, perPage: 30 },
      sort: { field: "created_at", order: "DESC" },
    },
    { enabled: isOwner, refetchInterval: 60_000 },
  );
  const { data: alerts = [], refetch: refetchAlerts } = useGetList<SocialAlert>(
    "gula_social_alerts",
    {
      filter: { "handled_at@is": null },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
    { enabled: isOwner, refetchInterval: 60_000 },
  );
  // Re-render every minute so "toca revisar" appears on time
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (!isOwner || !channels.length) return null;
  const everyHours = settings?.check_every_hours ?? 3;
  const cards = channels.map((channel) => {
    const lastCheck = checks.find((check) => check.channel === channel.key);
    const open = alerts.filter((alert) => alert.channel === channel.key);
    return {
      channel,
      lastCheck,
      alerts: open,
      status: socialStatus({
        active: channel.active,
        openAlerts: open.length,
        lastCheckAt: lastCheck?.created_at,
        everyHours,
        now,
      }),
      next: nextCheckAt(lastCheck?.created_at, everyHours, now),
    };
  });
  const pending = cards.filter(
    (card) => card.status === "due" || card.status === "alerts",
  ).length;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Inbox className="h-5 w-5" />
          Bandejas de redes
        </h2>
        <span className="text-sm text-muted-foreground">
          {pending
            ? `${pending} por revisar · cada ${everyHours} h`
            : `Todo al día · cada ${everyHours} h`}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cards.map((card) => (
          <ChannelCard
            key={card.channel.key}
            {...card}
            isAdmin={isAdmin}
            onChecked={() => {
              refetchChecks();
              refetchAlerts();
            }}
            onChannelChanged={() => refetchChannels()}
          />
        ))}
      </div>
    </section>
  );
};
